import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const WatchButton = ({ company }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [watch, setWatch] = useState(null);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      api.get("/watchlist").then((w) => setWatch(w.data.find((x) => x.slug === company.slug) || null)).catch(() => {});
    }).catch(() => setUser(false));
  }, [company.slug]);

  const save = async () => {
    if (!user) { sessionStorage.setItem("post_auth_redirect", `/company/${company.slug}`); navigate("/client/login"); return; }
    setBusy(true);
    try {
      const res = await api.post("/watchlist", { slug: company.slug, target_price: target ? Number(target) : null });
      setWatch(res.data);
      setOpen(false);
      toast.success(target ? `Alert set — we'll email you when ${company.name} crosses ₹${Number(target).toLocaleString("en-IN")}.` : `Watching ${company.name} — you'll be emailed on every price change.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/watchlist/${company.slug}`);
      setWatch(null);
      toast.success("Alert removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (watch) {
    return (
      <div data-testid="watch-active" className="flex flex-wrap items-center gap-3 border border-emerald-600/30 bg-emerald-600/[0.06] px-5 py-3">
        <Bell className="h-4 w-4 text-emerald-700" />
        <span className="font-mono2 text-[11px] uppercase tracking-[0.18em] text-emerald-800">
          {watch.target_price ? `Alert at ₹${Number(watch.target_price).toLocaleString("en-IN")}` : "Watching · every move"}
        </span>
        <button data-testid="watch-remove-button" onClick={remove} disabled={busy} className="ml-auto flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500 transition-colors hover:text-rose-700">
          <BellOff className="h-3.5 w-3.5" /> Stop
        </button>
      </div>
    );
  }

  return (
    <div data-testid="watch-widget" className="border border-slate-200 bg-[#F6F4FD]/70 p-5">
      {!open ? (
        <button data-testid="watch-open-button" onClick={() => (user ? setOpen(true) : save())} className="group flex items-center gap-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9] transition-colors hover:text-[#5B21B6]">
          <Bell className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" /> {user ? "Watch this share · email me on price moves" : "Sign in to set a price alert"}
        </button>
      ) : (
        <div className="space-y-4">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9]">Price alert for {company.name}</p>
          <p className="text-sm text-slate-500">Leave the target blank to be emailed on every indicative price change, or set a target price to be alerted only when it is crossed.</p>
          <div className="flex flex-wrap gap-3">
            <input
              data-testid="watch-target-input"
              type="number"
              min="0.01"
              step="0.01"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={`Target ₹ (optional) · now ₹${company.price}`}
              className="flex-1 min-w-[220px] border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3 font-mono2 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#6D28D9]/60"
            />
            <button data-testid="watch-save-button" onClick={save} disabled={busy} className="bg-[#6D28D9] px-6 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
              {busy ? "Saving…" : "Set alert"}
            </button>
            <button data-testid="watch-cancel-button" onClick={() => setOpen(false)} className="px-4 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-slate-900">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
